import React, { useMemo, useState } from "react";
import { useCart } from "../cart/CartContext";
import SchoolPicker from "./SchoolPicker";
import { LOGO_STATUS, resolveProductImage } from "../services/schoolsService";
import "./school-mockup.css";

const LOW_STOCK_THRESHOLD = 5;

export default function FirestoreProductCard({ product, defaultSchool = null }) {
  const { addItem } = useCart();
  // Personalized items carry a school chosen at purchase time. Everything
  // else behaves exactly as before.
  const needsSchool = product.requiresSchool === true;
  const [school, setSchool] = useState(defaultSchool);

  // A color hidden in the admin panel carries `active: false`, and a single
  // color/size combination carries `hidden: true`. Both are filtered out here
  // rather than deleted from the document, so hiding a Tank Top color takes
  // its swatch off the card while past orders that reference it still
  // resolve. `!== false` on purpose: every product saved before this field
  // existed has no `active` key at all and must keep showing.
  const visibleSizes = (v) => (v?.sizes || []).filter((s) => s.hidden !== true);

  const variants = (product.variants || []).filter(
    (v) => v.active !== false && (!product.hasSizes || visibleSizes(v).length > 0)
  );

  const [activeColorKey, setActiveColorKey] = useState(variants[0]?.colorKey);
  const [activeSize, setActiveSize] = useState(
    product.hasSizes ? visibleSizes(variants[0])[0]?.label || null : null
  );
  const [quantity, setQuantity] = useState(1);
  const [justAdded, setJustAdded] = useState(false);

  const variant = useMemo(
    () => variants.find((v) => v.colorKey === activeColorKey) || variants[0],
    [variants, activeColorKey]
  );

  if (!variant) return null; // product has no pricing set up yet — nothing to show

  const selectedSizeEntry = product.hasSizes
    ? visibleSizes(variant).find((s) => s.label === activeSize)
    : variant;

  const stockQty = product.hasSizes ? selectedSizeEntry?.stockQty : variant.stockQty;
  const isSoldOut = typeof stockQty === "number" && stockQty <= 0;
  const isLowStock = typeof stockQty === "number" && stockQty > 0 && stockQty <= LOW_STOCK_THRESHOLD;

  const canAdd =
    !isSoldOut &&
    (product.hasSizes ? Boolean(selectedSizeEntry?.priceId) : Boolean(variant.priceId)) &&
    (!needsSchool || Boolean(school));
  // Image priority: this school's real mockup for this colour, then any
  // mockup for this school, then the plain garment photo.
  const displayImage = resolveProductImage({ product, variant, school });

  // If there's no mockup yet but we DO have the school's logo, composite it
  // over the plain garment as a live preview. Saves photographing every
  // school x product x colour combination, and disappears the moment a real
  // mockup is uploaded for that combination.
  const usingGenericImage =
    displayImage === (variant.image || product.image);

  const overlay = product.logoOverlay || null;
  const showLogoOverlay =
    needsSchool &&
    usingGenericImage &&
    overlay?.enabled === true &&
    Boolean(school?.logoUrl);
  const displayPrice = product.hasSizes ? selectedSizeEntry?.price : variant.price;

  const handleAddToCart = () => {
    if (!canAdd) return;
    addItem({
      productId: product.id,
      colorKey: variant.colorKey,
      colorLabel: variant.label,
      size: activeSize || null,
      title: product.name,
      image: displayImage,
      price: displayPrice,
      stripePriceRef: product.hasSizes ? selectedSizeEntry?.priceId : variant.priceId,
      quantity,
      // Personalization travels with the line and is written onto the order.
      schoolId: school?.id || null,
      schoolName: school?.name || null,
      schoolState: school?.state || null,
      schoolLogoUrl: school?.logoUrl || null,
      needsArtwork: needsSchool
        ? !school?.logoUrl || school?.logoStatus === LOGO_STATUS.PENDING
        : false,
    });
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1500);
  };

  return (
    <div className="fs-product-card">
      <div className="fs-prod-image fs-prod-image-stack">
        {displayImage && <img src={displayImage} alt={`${product.name} - ${variant.label}`} />}

        {showLogoOverlay && (
          <img
            className="fs-logo-overlay"
            src={school.logoUrl}
            alt={`${school.name} logo`}
            style={{
              top: `${overlay.top ?? 32}%`,
              left: `${overlay.left ?? 50}%`,
              width: `${overlay.width ?? 30}%`,
            }}
          />
        )}

        {needsSchool && school && !usingGenericImage && (
          <span className="fs-mockup-badge">{school.name}</span>
        )}

        {isSoldOut && <span className="fs-sold-out-badge">Sold Out</span>}
      </div>

      <div className="fs-prod-info">
        <h3 className="fs-prod-title">{product.name}</h3>
        {product.description && <p className="fs-prod-dec">{product.description}</p>}
        {typeof displayPrice === "number" && displayPrice > 0 && (
          <div className="fs-prod-price">${displayPrice.toFixed(2)}</div>
        )}
        {isLowStock && <div className="fs-low-stock">Only {stockQty} left in stock!</div>}

        {/* Image options and color options are the same chooser wearing
            different clothes — one variant per swatch, either way. A product
            with image options paints thumbnails; everything else paints the
            color dots it always did. */}
        {(product.hasImageOptions || product.hasColors) && variants.length > 1 && (
          <div
            className={`fs-swatches ${product.hasImageOptions ? "fs-swatches--image" : ""}`}
            role="group"
            aria-label={product.hasImageOptions ? "Choose an option" : "Choose color"}
          >
            {variants.map((v) => {
              const isActive = activeColorKey === v.colorKey;
              const select = () => {
                setActiveColorKey(v.colorKey);
                setActiveSize(
                  product.hasSizes ? visibleSizes(v)[0]?.label || null : null
                );
              };

              if (product.hasImageOptions) {
                return (
                  <button
                    key={v.colorKey}
                    type="button"
                    className={`fs-swatch fs-swatch--image ${isActive ? "active" : ""}`}
                    onClick={select}
                    title={v.label}
                    aria-pressed={isActive}
                  >
                    <img src={v.image || product.image} alt={v.label} />
                  </button>
                );
              }

              return (
                <button
                  key={v.colorKey}
                  type="button"
                  className={`fs-swatch ${isActive ? "active" : ""}`}
                  style={{ backgroundColor: v.swatch || "#ccc" }}
                  onClick={select}
                  title={v.label}
                  aria-pressed={isActive}
                />
              );
            })}
          </div>
        )}

        {needsSchool && (
          <SchoolPicker
            value={school}
            onChange={setSchool}
            defaultSchool={defaultSchool}
            compact
          />
        )}

        {product.hasSizes && (
          <div className="fs-sizes" role="group" aria-label="Choose size">
            {visibleSizes(variant).map((s) => (
              <button
                key={s.label}
                className={`fs-size-pill ${activeSize === s.label ? "active" : ""}`}
                onClick={() => setActiveSize(s.label)}
                aria-pressed={activeSize === s.label}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        <div className="fs-atc-qty">
          <button type="button" onClick={() => setQuantity((q) => Math.max(1, q - 1))} aria-label="Decrease quantity">−</button>
          <span>{quantity}</span>
          <button type="button" onClick={() => setQuantity((q) => q + 1)} aria-label="Increase quantity">+</button>
        </div>

        <button
          type="button"
          className={`fs-btn-add-to-cart gradient-color ${canAdd ? "" : "disabled"}`}
          onClick={handleAddToCart}
          disabled={!canAdd}
          title={
            isSoldOut
              ? "Sold out"
              : needsSchool && !school
              ? "Choose a school to continue"
              : canAdd
              ? "Add to cart"
              : "Select a size to continue"
          }
        >
          {isSoldOut
            ? "Sold Out"
            : justAdded
            ? "Added ✓"
            : needsSchool && !school
            ? "Choose a school"
            : "Add to Cart"}
        </button>
      </div>
    </div>
  );
}
